import { useState } from "react";
import { downloadFile, buildDownloadFilename } from "../utils/downloadFile";

interface UseFileDownloadOptions {
  onError: (message: string) => void;
}

interface UseFileDownloadResult {
  /** Trigger a download for the given item. Ignores duplicate calls while one is already in flight. */
  download: (
    id: number,
    url: string,
    title: string | undefined,
    ext: string,
  ) => Promise<void>;
  /** Returns true while a download is in progress for the given id. */
  isDownloadingId: (id: number) => boolean;
  /** Whole-number percent downloaded so far, or null when unknown / not downloading. */
  percentOf: (id: number) => number | null;
}

/**
 * Manages per-row file downloads with:
 * - In-progress tracking per item id (prevents double-clicks)
 * - Error reporting via onError callback
 */
export const useFileDownload = ({
  onError,
}: UseFileDownloadOptions): UseFileDownloadResult => {
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set());
  const [percents, setPercents] = useState<Map<number, number>>(new Map());

  const download = async (
    id: number,
    url: string,
    title: string | undefined,
    ext: string,
  ): Promise<void> => {
    if (downloadingIds.has(id)) return;

    setDownloadingIds((prev) => new Set(prev).add(id));

    const filename = buildDownloadFilename(title, id, ext);

    try {
      await downloadFile(url, filename, (fraction) => {
        const pct = Math.floor(fraction * 100);
        // only re-render when the whole-number percent changes, not on every chunk
        setPercents((prev) => (prev.get(id) === pct ? prev : new Map(prev).set(id, pct)));
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      onError(`Failed to download: ${msg}`);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setPercents((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const isDownloadingId = (id: number): boolean => downloadingIds.has(id);
  const percentOf = (id: number): number | null => percents.get(id) ?? null;

  return { download, isDownloadingId, percentOf };
};
